import type { AnalyseCapture } from './image';
import type { Coins } from './types';

/** Un travailleur par calcul, fermé aussi en cas d'annulation ou de dépassement du délai. */
async function calculer<T>(
  action: 'analyser' | 'produire',
  fichier: Blob,
  coins?: Coins,
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted) throw new DOMException('Traitement annulé', 'AbortError');
  if (typeof Worker !== 'function' || typeof OffscreenCanvas !== 'function') {
    const module = await import('./image');
    return (
      action === 'analyser'
        ? module.analyserCapture(fichier, coins)
        : module.produireCapture(fichier, coins!)
    ) as Promise<T>;
  }
  return new Promise<T>((resolve, reject) => {
    const travailleur = new Worker(new URL('./travailleur.ts', import.meta.url), {
      type: 'module',
    });
    const finir = () => {
      clearTimeout(delai);
      signal?.removeEventListener('abort', annuler);
      travailleur.terminate();
    };
    const annuler = () => {
      finir();
      reject(new DOMException('Traitement annulé', 'AbortError'));
    };
    const delai = setTimeout(() => {
      finir();
      reject(new Error('Traitement trop long : utilisez une photo plus petite'));
    }, 60000);
    signal?.addEventListener('abort', annuler, { once: true });
    travailleur.onmessage = (e: MessageEvent<{ resultat?: T; erreur?: string }>) => {
      finir();
      if (e.data.erreur) reject(new Error(e.data.erreur));
      else resolve(e.data.resultat as T);
    };
    travailleur.onerror = () => {
      finir();
      reject(new Error('Traitement de la photo interrompu'));
    };
    travailleur.postMessage({ action, fichier, coins });
  });
}
export const analyserPhoto = (fichier: Blob, coins?: Coins, signal?: AbortSignal) =>
  calculer<AnalyseCapture>('analyser', fichier, coins, signal);
export const produirePhoto = (fichier: Blob, coins: Coins, signal?: AbortSignal) =>
  calculer<Blob>('produire', fichier, coins, signal);
